import React from "react";
import { Check, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCopyFlowLink } from "./useCopyFlowLink";

interface CopyFlowLinkButtonProps {
  borrowerId: number;
  flowId: number;
  className?: string;
  /**
   * Icon over the link. The parent needs `group relative` so it can appear on hover; on a touch
   * screen it always shows.
   */
  appearance?: "labeled" | "icon";
}

/** The repo's mouse query (AssistantHeader): a touch screen that reports hover still shows it. */
const HIDDEN_UNTIL_HOVER = "[@media(hover:hover)_and_(pointer:fine)]:opacity-0";

const CopyFlowLinkButton: React.FC<CopyFlowLinkButtonProps> = ({
  borrowerId,
  flowId,
  className,
  appearance = "labeled",
}) => {
  const { copy, state } = useCopyFlowLink(borrowerId, flowId);
  const Icon = state.status === "pending" ? Loader2 : state.status === "copied" ? Check : Copy;
  const label = state.status === "copied" ? "Copied" : "Copy link";
  const iconOnly = appearance === "icon";

  const button = (
    <Button
      type="button"
      size={iconOnly ? "icon" : "sm"}
      variant={iconOnly ? "ghost" : "outline"}
      disabled={state.status === "pending"}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={cn(
        iconOnly &&
          "absolute right-1 top-1.5 h-6 w-6 bg-background/80 text-muted-foreground transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 [&_svg]:size-3.5",
        // Hidden until hover or focus only with a mouse; touch screens always show it. Busy,
        // copied or failed, it stays.
        iconOnly && state.status === "idle" && HIDDEN_UNTIL_HOVER
      )}
      onClick={() => void copy()}
    >
      <Icon className={cn(state.status === "pending" && "animate-spin")} />
      {iconOnly ? null : label}
    </Button>
  );

  const error =
    state.status === "error" ? (
      <p role="alert" className={cn("text-xs text-destructive", iconOnly && "mt-1")}>
        {state.message}
      </p>
    ) : null;

  if (iconOnly) {
    return (
      <>
        {button}
        {error}
      </>
    );
  }

  return (
    <div className={cn("flex flex-col items-start gap-1", className)}>
      {button}
      {error}
    </div>
  );
};

export default CopyFlowLinkButton;
